/*
 * DER SATZ NEBEN EINER FRIST.
 * ===========================================================================
 *
 * ORIGINALTEXT MARVIN (14.09.2026, zum Bildschirmfoto der Behördenmappe)
 *   „1 Tage abgelaufen"
 *
 * Er zitiert, was dastand. Zwei Dinge daran waren falsch, und das zweite ist
 * das teurere.
 *
 * DER WORTLAUT: „1 Tage" ist kein Satz, sondern eine Zeichenkette mit einer
 * Zahl davor. Die Form hängt an der ZAHL, und sie steht ab jetzt an einer
 * Stelle - `dauerText` im Terminraster rechnet dieselbe Form aus und ruft
 * sie hier, statt sie ein zweites Mal hinzuschreiben.
 *
 * DIE RECHNUNG: die Behördenmappe hat die Resttage SELBST gerechnet, gegen
 * eine festgenagelte 7, und ohne die eine Regel, die es dafür gibt
 * (`regeln/termine.ts: vaoampel`). Die Übersicht daneben hat sie gefragt.
 * **Zwei Fassungen derselben Rechnung laufen beim ersten Nachtrag
 * auseinander** - und dann steht dieselbe Frist auf dem einen Bildschirm rot
 * und auf dem anderen grün. Gerechnet wird sie jetzt auf dem SERVER, einmal,
 * und hier wird nur noch geschrieben, was herauskommt.
 *
 * DESHALB STEHT DIESE DATEI OHNE JSX DANEBEN: eine Datei mit JSX lässt sich
 * mit `--experimental-strip-types` nicht laden, und dann bliebe nur die
 * Textsuche - und eine Textsuche misst, dass etwas dasteht, nicht, dass es
 * stimmt.
 */

/** Wie `Vaoampel` in `api/src/regeln/termine.ts`. */
export type Fristampel = 'gruen' | 'warnung' | 'abgelaufen' | 'ohne';

export interface Fristlage {
  ampel: Fristampel;
  /** Kalendertage bis zum Ende, negativ wenn vorbei. NULL heisst: keine. */
  resttage: number | null;
}

/**
 * „1 Tag", „3 Tage", „1,5 Tage".
 *
 * DIE ZAHL ENTSCHEIDET DIE FORM, DER TEXT STEHT DANEBEN. Beides getrennt,
 * weil eine Dauer mit Dezimalkomma geschrieben wird („1,5 Tage") und eine
 * Frist in ganzen Tagen - die FORM ist in beiden Fällen dieselbe Frage.
 */
export function tageText(n: number, wort = String(n)): string {
  return `${wort} ${n === 1 ? 'Tag' : 'Tage'}`;
}

/**
 * Der Satz, der neben der Frist steht.
 *
 * VIER LAGEN UND NICHT ZWEI. „abgemeldet" ist der Fall, um den es Marvin
 * geht: liegt die Fertigstellungsanzeige vor, ist die Baustelle abgemeldet,
 * und dann ist eine abgelaufene Anordnung keine Mahnung mehr, sondern
 * Geschichte. Eine leere Zeichenkette heisst: hier steht keine Frist - das
 * ist bei jedem eigenen Papier so und kein Mangel.
 */
export function fristText(l: Fristlage): string {
  if (l.ampel === 'ohne') return '';
  /* Die Ampel steht auf grün UND es sind keine Tage mehr zu zählen: das kann
     nur die Fertigstellungsanzeige gewesen sein (`vaoampel`). */
  if (l.resttage === null) return 'abgemeldet, die FAZ liegt vor';
  if (l.resttage < 0) return `${tageText(-l.resttage)} abgelaufen`;
  if (l.resttage === 0) return 'läuft heute ab';
  return `noch ${tageText(l.resttage)}`;
}

/**
 * Die Klasse der Marke daneben.
 *
 * SIE FOLGT DER AMPEL UND KEINER EIGENEN ZAHL. Hier stand `tage <= 7` - eine
 * festgenagelte 7 neben einem Schlüssel, den ein Mensch stellt
 * (`termin.vao_warnfenster_tage`). Wer ihn auf 14 setzt, hat den Bildschirm
 * damit ab jetzt auch eingestellt.
 */
export function fristKlasse(l: Fristlage): string {
  if (l.ampel === 'abgelaufen') return 'fehler';
  if (l.ampel === 'warnung') return 'warnung';
  if (l.ampel === 'gruen' && l.resttage === null) return 'gruen';
  if (l.ampel === 'ohne') return 'leise';
  return '';
}

/**
 * Läuft diese Frist noch?
 *
 * GEZÄHLT WIRD, WAS NOCH JEMANDEN ANGEHT. Eine abgemeldete Baustelle zählt
 * nicht mit (sonst stünde in der Kachel eine Zahl, hinter der nichts mehr zu
 * tun ist), eine abgelaufene auch nicht - die hat ihre eigene Farbe.
 */
export function laeuftNoch(l: Fristlage): boolean {
  return l.resttage !== null && l.resttage >= 0 && l.ampel !== 'ohne';
}
